export const MAIN_UNIT_SELECTORS = {
    Type: ['Residential', 'Commercial', 'Industrial'] as const,
    unitCategories: {
        Residential: [
            'Apartment',
            'Townhouse',
            'Villa Compound',
            'Land',
            'Building',
            'Villa',
            'Penthouse',
            'Hotel Apartment',
            'Floor',
            'Studio',
            'Loft'
        ],
        Commercial: [
            'Office',
            'Villa',
            'Building',
            'Showroom',
            'Shop',
            'Floor',
            'Other Commercial',
            'Retail Space',
            'Clinic',
            'Restaurant',
            'Hotel',
            'School'
        ],
        Industrial: [
            'Warehouse',
            'Factory',
            'Labour Camp',
            'Industrial Land',
            'Mixed Use Land',
            'Bulk Unit',
            'Land'
        ]
    } as const,
    selectorsByType: {
        Residential: {
            Fit: ['Furnished', 'Semi Furnished', 'Not furnished'],
            View: ['Water', 'Landmark', 'Skyline', 'Park', 'Sea', 'Beach', 'Desert', 'Burj Khalifa'],
            Features: [
                'Close to Metro',
                'Close to Day Care Center',
                'Close to First Aid Medical Center',
                'Close to ATM Facility',
                'Close to Cafeteria or Canteen'
            ],
            Amenities: [
                'Central A/C & Heating',
                'Private Garden',
                'Private Pool',
                'Private Gym',
                'Private Jacuzzi',
                'Shared Pool',
                'Shared Spa',
                'Shared Gym',
                'Security',
                'Maid Service',
                'Covered Parking',
                'Pets Allowed',
                'Electricity Backup',
                'Broadband Internet',
                'Satellite / Cable TV',
                'Intercom',
                'Maintenance Staff',
                'CCTV Security',
                'Cleaning Services',
                'Lobby in Building',
                'Barbeque Area'
            ],
            Appliances: [
                'Walk-in Closet',
                'Balcony',
                'Maids Room',
                'Laundry Room',
                'Shared Kitchen',
                'Kids Play Area',
                'Barbeque Area',
                'Storage Areas',
                'Waste Disposal',
                'Built-in Wardrobes',
                'Built-in Kitchen Appliances',
                'Double Glazed Windows'
            ]
        },
        Commercial: {
            Fit: ['Furnished', 'Semi Furnished', 'Not furnished', 'Fitted', 'Shell & Core'],
            View: ['Water', 'Landmark', 'Skyline', 'Park', 'Sea', 'Beach', 'Desert', 'Burj Khalifa'],
            Features: [
                'Close to Metro',
                'Close to Business Center',
                'Close to First Aid Medical Center',
                'Close to ATM Facility',
                'Close to Facilities for Disabled',
                'Close to Cafeteria or Canteen'
            ],
            Amenities: [
                'Central A/C & Heating',
                'Concierge Service',
                'Shared Gym',
                'Security',
                'Covered Parking',
                'Electricity Backup',
                'Service Elevators',
                'Broadband Internet',
                'Satellite / Cable TV',
                'Intercom',
                'Maintenance Staff',
                'CCTV Security',
                'Cleaning Services',
                'Lobby in Building'
            ],
            Appliances: [
                'Reception / Waiting Room',
                'Prayer Room',
                'Storage Areas',
                'Waste Disposal',
                'Double Glazed Windows'
            ]
        },
        Industrial: {
            Fit: ['Not furnished', 'Fitted', 'Shell & Core'],
            View: ['Landmark', 'Skyline', 'Park', 'Desert'],
            Features: [
                'Close to Metro',
                'Close to Business Center',
                'Close to First Aid Medical Center',
                'Close to ATM Facility',
                'Close to Facilities for Disabled',
                'Close to Cafeteria or Canteen'
            ],
            Amenities: [
                'Security',
                'Covered Parking',
                'Electricity Backup',
                'Service Elevators',
                'Maintenance Staff',
                'CCTV Security',
                'Lobby in Building'
            ],
            Appliances: [
                'Reception / Waiting Room',
                'Prayer Room',
                'Storage Areas',
                'Waste Disposal',
                'Double Glazed Windows'
            ]
        }
    } as const,
    categoryOverrides: {
        Land: {
            Fit: [],
            View: [],
            Appliances: [],
            Amenities: []
        },
        'Industrial Land': {},
        'Mixed Use Land': {
            Fit: [],
            View: [],
            Appliances: [],
            Amenities: []
        },
        Factory: {
            Appliances: ['Storage Areas', 'Waste Disposal'],
            Amenities: ['Security', 'Electricity Backup', 'Maintenance Staff', 'CCTV Security']
        },
        Warehouse: {
            Appliances: ['Storage Areas', 'Waste Disposal'],
            Amenities: ['Security', 'Covered Parking', 'CCTV Security']
        },
        'Labour Camp': {
            Appliances: ['Shared Kitchen', 'Laundry Room', 'Storage Areas', 'Waste Disposal'],
            Amenities: ['Security', 'Covered Parking', 'Electricity Backup', 'Maintenance Staff', 'CCTV Security']
        },
        'Bulk Unit': {
            Appliances: ['Storage Areas', 'Waste Disposal']
        }
    } as const
}

export type MainUnitType = typeof MAIN_UNIT_SELECTORS.Type[number]
export type MainUnitSelectorKey = 'Fit' | 'View' | 'Features' | 'Amenities' | 'Appliances'
export type UnitInfoApplicabilityField =
    | 'Bedrooms'
    | 'Bathrooms'
    | 'GuestBathroom'
    | 'MaidsRoom'
    | 'ACCount'
    | 'ParkingSlotsAmount'
    | 'Features'
    | 'Amenities'
    | 'Appliances'
    | 'Fit'
    | 'View'

export const UNIT_INFO_APPLICABILITY_FIELDS: UnitInfoApplicabilityField[] = [
    'Bedrooms',
    'Bathrooms',
    'GuestBathroom',
    'MaidsRoom',
    'ACCount',
    'ParkingSlotsAmount',
    'Features',
    'Amenities',
    'Appliances',
    'Fit',
    'View'
]

export const isMainUnitType = (value: unknown): value is MainUnitType => (
    typeof value === 'string' && MAIN_UNIT_SELECTORS.Type.includes(value as MainUnitType)
)

export const getUnitCategoriesByType = (type: unknown): string[] => (
    isMainUnitType(type) ? [...MAIN_UNIT_SELECTORS.unitCategories[type]] : []
)

export const getSelectorsForTypeAndCategory = (
    type: unknown,
    category: unknown,
    selectorKey: MainUnitSelectorKey
): string[] => {
    if (!isMainUnitType(type)) return []
    const baseValues = MAIN_UNIT_SELECTORS.selectorsByType[type][selectorKey] ?? []
    if (typeof category !== 'string') return [...baseValues]
    const overrides = MAIN_UNIT_SELECTORS.categoryOverrides[category as keyof typeof MAIN_UNIT_SELECTORS.categoryOverrides]
    if (overrides == null) return [...baseValues]
    const overrideMap = overrides as Record<string, string[] | undefined>
    const overrideValues = overrideMap[selectorKey]
    return Array.isArray(overrideValues) ? [...overrideValues] : [...baseValues]
}

export const isUnitLandLikeCategory = (category: unknown): boolean => (
    typeof category === 'string' && /\bland\b/i.test(category.trim())
)

export const getUnitEnvironmentSelectors = (type: unknown, category: unknown): Record<MainUnitSelectorKey, string[]> => ({
    Fit: getSelectorsForTypeAndCategory(type, category, 'Fit'),
    View: getSelectorsForTypeAndCategory(type, category, 'View'),
    Features: getSelectorsForTypeAndCategory(type, category, 'Features'),
    Amenities: getSelectorsForTypeAndCategory(type, category, 'Amenities'),
    Appliances: getSelectorsForTypeAndCategory(type, category, 'Appliances')
})

export const getUnitFitOptions = (type: unknown, category: unknown): string[] => (
    getSelectorsForTypeAndCategory(type, category, 'Fit')
)

export const getUnitViewOptions = (type: unknown, category: unknown): string[] => (
    getSelectorsForTypeAndCategory(type, category, 'View')
)

export const getUnitFeaturesOptions = (type: unknown, category: unknown): string[] => (
    getSelectorsForTypeAndCategory(type, category, 'Features')
)

export const getUnitAmenitiesOptions = (type: unknown, category: unknown): string[] => (
    getSelectorsForTypeAndCategory(type, category, 'Amenities')
)

export const getUnitAppliancesOptions = (type: unknown, category: unknown): string[] => (
    getSelectorsForTypeAndCategory(type, category, 'Appliances')
)

export const resolveUnitInfoFieldApplicability = (
    type: unknown,
    category: unknown
): Record<UnitInfoApplicabilityField, boolean> => {
    const isResidential = type === 'Residential'
    const isLandLike = isUnitLandLikeCategory(category)
    const environmentSelectors = getUnitEnvironmentSelectors(type, category)
    const hasEnvironmentSelectors = (key: MainUnitSelectorKey): boolean => (
        !isLandLike && environmentSelectors[key].length > 0
    )

    return {
        Bedrooms: !isLandLike,
        Bathrooms: !isLandLike,
        GuestBathroom: isResidential && !isLandLike,
        MaidsRoom: isResidential && !isLandLike,
        ACCount: !isLandLike,
        ParkingSlotsAmount: !isLandLike,
        Features: hasEnvironmentSelectors('Features'),
        Amenities: hasEnvironmentSelectors('Amenities'),
        Appliances: hasEnvironmentSelectors('Appliances'),
        Fit: hasEnvironmentSelectors('Fit'),
        View: hasEnvironmentSelectors('View')
    }
}

export const normalizeCreateUnitInfoForStorage = <T extends Record<string, any>>(
    data: T,
    type: unknown,
    category: unknown
): T => {
    const normalized = { ...data } as Record<string, any>
    const applicability = resolveUnitInfoFieldApplicability(type, category)

    if (!applicability.Bedrooms) normalized.Bedrooms = null
    if (!applicability.Bathrooms) normalized.Bathrooms = null
    if (!applicability.GuestBathroom) normalized.GuestBathroom = false
    if (!applicability.MaidsRoom) normalized.MaidsRoom = false
    if (!applicability.ACCount) normalized.ACCount = null
    if (!applicability.ParkingSlotsAmount) normalized.ParkingSlotsAmount = null
    if (!applicability.Features) normalized.Features = []
    if (!applicability.Amenities) normalized.Amenities = []
    if (!applicability.Appliances) normalized.Appliances = []
    if (!applicability.Fit) normalized.Fit = null
    if (!applicability.View) normalized.View = null

    return normalized as T
}
